'use client'

import React, { useMemo } from 'react'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Loader2 } from 'lucide-react'
import { ChartTooltip } from './ChartTooltip'
import { parseDbTimestamp } from '@/lib/dates'

// Top row of CTT Pilot Overview: entries per day and their running total in
// the period, one line per source (the vendor feed first; more sources are
// more entries in the caller's series list). Purely presentational — the
// caller owns the period (from/to, local days), each series' timestamps and
// the replay reveal.

// Categorical slots in fixed order (dataviz palette), light / dark steps. A
// series keeps its slot, so adding or hiding another never repaints it.
export const SERIES_COLORS: Array<{ light: string; dark: string }> = [
  { light: '#2a78d6', dark: '#3987e5' }, // blue
  { light: '#eb6834', dark: '#d95926' }, // orange
  { light: 'var(--success)', dark: '#199e70' }, // aqua
  { light: '#eda100', dark: '#c98500' }, // yellow
  { light: '#e87ba4', dark: '#d55181' }, // magenta
  { light: '#008300', dark: '#008300' }, // green
  { light: '#6250d6', dark: '#9085e9' }, // violet
  { light: 'var(--destructive)', dark: '#e66767' }, // red
]

export type SourceLine = {
  /** Stable id, also the chart dataKey. */
  key: string
  label: string
  /** Index into the categorical palette (0 = blue). */
  slot: number
  /** created_at values (DB timestamps, UTC without offset). */
  timestamps: string[]
  loading?: boolean
  error?: string | null
}

function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function dayLabel(key: string): string {
  const [, m, d] = key.split('-')
  return `${Number(d)}/${Number(m)}`
}

export function SourceLinesTopRow({
  from,
  to,
  series,
  revealThrough,
  isDark,
}: {
  /** Period, inclusive local days (YYYY-MM-DD). */
  from: string
  to: string
  series: SourceLine[]
  /** During a replay: last local day reached; later days stay empty so the
   * lines grow as the replay advances. null/undefined = show every day. */
  revealThrough?: string | null
  isDark: boolean
}) {
  const { daily, cumulative, totals, days } = useMemo(() => {
    const counts = series.map((s) => {
      const m = new Map<string, number>()
      for (const iso of s.timestamps) {
        const d = parseDbTimestamp(iso)
        if (!d) continue
        const key = dayKey(d)
        m.set(key, (m.get(key) ?? 0) + 1)
      }
      return m
    })
    const daily: Record<string, string | number | null>[] = []
    const cumulative: Record<string, string | number | null>[] = []
    const running = series.map(() => 0)
    let days = 0
    // Step by calendar day so a DST change can't skip or repeat a day.
    const end = new Date(to + 'T00:00')
    for (let d = new Date(from + 'T00:00'); d <= end; d.setDate(d.getDate() + 1)) {
      const key = dayKey(d)
      const hidden = !!revealThrough && key > revealThrough
      const dayRow: Record<string, string | number | null> = { date: dayLabel(key) }
      const cumRow: Record<string, string | number | null> = { date: dayLabel(key) }
      series.forEach((s, i) => {
        if (hidden) {
          dayRow[s.key] = null
          cumRow[s.key] = null
          return
        }
        const count = counts[i].get(key) ?? 0
        running[i] += count
        dayRow[s.key] = count
        cumRow[s.key] = running[i]
      })
      daily.push(dayRow)
      cumulative.push(cumRow)
      days++
    }
    return { daily, cumulative, totals: running, days }
  }, [series, from, to, revealThrough])

  const lines = series.map((s, i) => ({
    key: s.key,
    label: s.label,
    color: SERIES_COLORS[s.slot % SERIES_COLORS.length][isDark ? 'dark' : 'light'],
    total: totals[i],
  }))
  const loading = series.some((s) => s.loading)
  const error = series.map((s) => s.error).find(Boolean) ?? null

  return (
    <div className="col-span-full grid grid-cols-1 gap-2 md:grid-cols-2">
      <LinesCard title="Entries per day" note={`${days} days`} data={daily} lines={lines} loading={loading} error={error} isDark={isDark} />
      <LinesCard title="Cumulative in period" note={`${days} days`} data={cumulative} lines={lines} loading={loading} error={error} isDark={isDark} />
    </div>
  )
}

function LinesCard({
  title,
  note,
  data,
  lines,
  loading,
  error,
  isDark,
}: {
  title: string
  note: string
  data: Record<string, any>[]
  lines: Array<{ key: string; label: string; color: string; total: number }>
  loading?: boolean
  error?: string | null
  isDark: boolean
}) {
  const cardBg = 'bg-card'
  const border = 'border-border'
  const muted = 'text-subtle'
  const text = 'text-foreground'
  const label = 'text-muted-foreground'
  const axis = 'var(--subtle-text)'
  const grid = 'var(--border)'
  const labelMap = Object.fromEntries(lines.map((l) => [l.key, l.label]))

  return (
    <div className={`flex h-60 flex-col overflow-hidden border ${border} ${cardBg}`}>
      <div className="flex items-start justify-between px-3 pt-2">
        <div className={`flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest ${muted}`}>
          {title}
          {loading && <Loader2 className="h-3 w-3 animate-spin" />}
        </div>
        <div className={`font-mono text-[9px] tabular-nums ${error ? 'text-destructive' : muted}`}>{error ?? note}</div>
      </div>
      {/* Legend, one item per line with its period total: the dot carries the
          series colour, the text stays in text tokens. */}
      <div className="mt-1 flex flex-wrap items-baseline gap-x-4 gap-y-1 px-3">
        {lines.map((l) => (
          <div key={l.key} className="flex items-center gap-1.5">
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: l.color }} />
            <span className={`font-mono text-[10px] ${label}`}>{l.label}</span>
            <span className={`font-mono text-lg font-semibold tabular-nums leading-none ${text}`}>{l.total}</span>
          </div>
        ))}
      </div>
      <div className="min-h-0 flex-1 pt-2">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 16, left: -20, bottom: 4 }}>
            <CartesianGrid vertical={false} stroke={grid} />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: axis }} tickLine={false} axisLine={false} minTickGap={24} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: axis }} tickLine={false} axisLine={false} width={40} />
            <Tooltip
              cursor={{ stroke: axis, strokeWidth: 1, strokeDasharray: '3 3' }}
              content={<ChartTooltip isDark={isDark} labelMap={labelMap} />}
            />
            {lines.map((l) => (
              <Line
                key={l.key}
                type="monotone"
                dataKey={l.key}
                stroke={l.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--card)' }}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
